import { Entity } from '@/engine/core/entity.ts';
import { UniqueEntityID } from '@/engine/core/uniqueEntityId.ts';
import { ViewPortKind } from '../../interfaces.ts';

import type {
  IObjectViewModel,
  IViewPortOrientation,
  IImageSliceViewModel,
  MeasurementKind,
  IMeasurementViewModel,
  OrientationPresetKind,
  TuneControlsDisclaimerType,
  ISelectableItem,
  ICenterLineViewModel,
  JSONConfig,
  IClonable,
  ViewOrientationKind,
  TuneControlsKind,
  ObjectsKind,
  ViewOptionsStruct,
  IBaseViewModel,
  GroupKind,
  DescriptorMapItem,
  ViewOption,
  IImagesRoiConfig
} from '../../interfaces.ts';
import { ViewPortType } from '../../interfaces.ts';
import type { SliceType } from '../../modules/images/interfaces.ts';
import { action, computed } from 'mobx';
import type { ViewPortTooltipViewModel } from '../ViewPortTooltipViewModel.ts';
import type * as THREE from 'three';
import type { WFIndication } from '@/surgeries/cardiac/TTVR/configs/views/_wfIndications.config.ts';
import type { IMeasurementBoxDetails } from '@/surgeries/cardiac/TTVR/components/boxes/measurementBoxes/VariableDistanceMeasurementBox/interfaces.ts';

export interface IImageViewPortDetails {
  sliceDescriptor: SliceType;
}

export interface IThreeDViewPortDetails {
  orientation: IViewPortOrientation;
  frontOrientationDescriptor: string;
  setOrientation: (orientation: IViewPortOrientation) => void;
}

export interface ViewPortInfo {
  descriptor: string;
  kind: ViewPortKind;
  details: IImageViewPortDetails | IThreeDViewPortDetails;
}

export interface ObjectBoxInfo {
  kind: ObjectsKind;
  label: string;
  order: number;
  group?: string;
  objects: Array<IObjectViewModel>;
  details?: Array<string>;
}

export interface MeasurementBoxInfo {
  kind: MeasurementKind;
  label: string;
  order: number;
  group?: string;
  measurements: Array<IMeasurementViewModel>;
  hasCriteria: boolean | undefined;
  details?: Array<string>;
}

export interface GroupInfo {
  label: string;
  descriptor: string;
  order: number;
  kind: GroupKind;
  isExpanded?: boolean;
}

export interface TuneControlInfo {
  kind: TuneControlsKind;
  label: string;
  units: string;
  order: number;
  group?: string;
  subUnits?: { value: string; ratio: number };
  descriptor: string | Array<string>;
  locatableDescriptors?: Array<string>;
  hasReset?: boolean;
  disclaimerType?: TuneControlsDisclaimerType;
}

export interface IOrientationPreset {
  label: string;
  descriptor: string;
  kind: OrientationPresetKind;
  setLabel: (label: string) => void;
}

export type ViewHelper = JSONConfig.Helper;

export interface IVisibilityDependency {
  item: string;
  dependsOn: Array<string>;
}

export interface ISelectionDependency {
  item: string;
  dependsOn: Array<string>;
}

export interface Cursor3D {
  coordinates: THREE.Vector3;
  direction: THREE.Vector3;
}

export interface ViewProps {
  descriptor: string;
  label: string;
  sideLabel: string;
  imageStackDescriptor: string;
  objects: Array<IObjectViewModel>;
  imageSlices: Array<IImageSliceViewModel>;
  clippingPlanes: Map<ViewPortType, THREE.Plane>;
  imagesRoiConfig?: IImagesRoiConfig;
  viewOrientationDescriptor: ViewOrientationKind;
  fluoroCurvePlaneDescriptor?: string;
  viewPorts: Array<ViewPortInfo>;
  measurementBoxes: Array<MeasurementBoxInfo>;
  planningEntitiesGroups: Array<GroupInfo>;
  objectBoxes: Array<ObjectBoxInfo>;
  workflowIndications: Array<WFIndication>;
  tuneControls: Array<TuneControlInfo>;
  measurements: Array<IMeasurementViewModel>;
  ghostCursor: Cursor3D | undefined;
  syncViewsDescriptor: string | undefined;
  centerLines: Array<ICenterLineViewModel>;
  activeCenterLineDescriptor: string | undefined;
  availabilityDescriptor: string | undefined;
  viewOptions: ViewOptionsStruct;
  orientationPresets: Array<IOrientationPreset>;
  visibilityDependencies: Array<IVisibilityDependency>;
  selectionDependencies: Array<ISelectionDependency>;
  approvedStatus: boolean;
  helpers: Array<JSONConfig.Helper>;
  viewPortsTooltips: Array<ViewPortTooltipViewModel>;
  collapseImages: boolean | undefined;
  cameraOrientationDescriptor?: ViewOrientationKind;
  cameraFittingFactor: number;
  descriptorsMapping?: Array<DescriptorMapItem>;
  descriptor2ViewOption?: Record<string, ViewOption>;
}

export class ViewEntity extends Entity<ViewProps> implements IClonable<ViewEntity> {
  @action
  public addObject(object: IObjectViewModel): void {
    const objectExists = this.props.objects.some((obj) => obj.descriptor === object.descriptor);

    if (objectExists) {
      console.warn('Object is already exist');
      return;
    }

    this.props.objects.push(object);
  }

  @action
  public addObjectToBox(object: IObjectViewModel, kind: ObjectsKind): void {
    const objectBoxes: Array<ObjectBoxInfo> = this.getObjectBoxes();
    const customObjectBox: ObjectBoxInfo | undefined = objectBoxes?.find(
      (box: ObjectBoxInfo): boolean => box.kind === kind
    );
    customObjectBox?.objects.push(object);
  }

  @action
  public removeObject(object: IObjectViewModel): void {
    const idx: number = this.props.objects.indexOf(object);
    if (idx > -1) {
      this.props.objects.splice(idx, 1);
    }
  }

  @action
  public removeObjectById(descriptor: string): void {
    const idx: number = this.props.objects.findIndex((object) => object.descriptor === descriptor);
    if (idx > -1) {
      this.props.objects.splice(idx, 1);
    }
  }

  @action
  public removeObjectFromBox(object: IObjectViewModel, kind: ObjectsKind): void {
    const objectBoxInfos: Array<ObjectBoxInfo> = this.getObjectBoxes();
    const customCommissurePointsBox: ObjectBoxInfo | undefined = objectBoxInfos?.find(
      (box: ObjectBoxInfo): boolean => box.kind === kind
    );
    const idx: number | undefined = customCommissurePointsBox?.objects.indexOf(object);

    if (idx === -1 || idx === undefined) return;
    else customCommissurePointsBox?.objects.splice(idx, 1);
  }

  @action
  public addMeasurement(measurement: IMeasurementViewModel): void {
    const measurementExists = this.props.measurements.some((m) => m.descriptor === measurement.descriptor);
    if (measurementExists) {
      console.warn('Measurement is already exist');
      return;
    }
    this.props.measurements.push(measurement);
  }

  @action
  public updateClippingPlane(descriptor: ViewPortType, plane: THREE.Plane | null): void {
    if (plane !== null) {
      if (descriptor === ViewPortType.ThreeD) {
        this.props.clippingPlanes.clear(); // Clear all other planes if ThreeD is being updated
      } else {
        this.props.clippingPlanes.delete(ViewPortType.ThreeD); // Remove the ThreeD plane if the updated plane is not ThreeD
      }
      this.props.clippingPlanes.delete(descriptor);
      this.props.clippingPlanes.set(descriptor, plane);
    } else {
      this.props.clippingPlanes.delete(descriptor);
    }
  }

  @action
  public addMeasurementToBox(measurement: IMeasurementViewModel, kind: MeasurementKind): void {
    const measurementBoxes: Array<MeasurementBoxInfo> = this.getMeasurementBoxes();
    const variableDistanceBox: MeasurementBoxInfo | undefined = measurementBoxes
      ?.filter((box: MeasurementBoxInfo): boolean => box.kind === kind)
      .find((box) => box.group === (measurement as IMeasurementBoxDetails).details.boxGroup);

    variableDistanceBox?.measurements.push(measurement);
  }

  @action
  public addWorkflowIndication(descriptor: string): void {
    this.props.workflowIndications.push({ descriptor: descriptor });
  }

  @action
  public removeWorkflowIndication(descriptor: string): void {
    const idx: number = this.props.workflowIndications.findIndex((indication) => indication.descriptor == descriptor);
    if (idx > -1) {
      this.props.workflowIndications.splice(idx, 1);
    }
  }

  @action
  public addMeasurementVisibilityDependencies(measurement: IMeasurementViewModel, object: IObjectViewModel): void {
    const visibilityDependencies = this.getVisibilityDependencies();

    visibilityDependencies?.push({
      item: object.descriptor,
      dependsOn: [measurement.descriptor]
    });
  }

  @action
  public removeMeasurement(measurement: IMeasurementViewModel): void {
    const idx: number = this.props.measurements.indexOf(measurement);
    if (idx > -1) {
      this.props.measurements.splice(idx, 1);
    }
  }

  @action
  public removeMeasurementFromBox(measurement: IMeasurementViewModel, kind: MeasurementKind): void {
    const measurementBoxes: Array<MeasurementBoxInfo> = this.getMeasurementBoxes();
    const variableDistanceBox: MeasurementBoxInfo | undefined = measurementBoxes
      ?.filter((box: MeasurementBoxInfo): boolean => box.kind === kind)
      .find((box) => box.group === (measurement as IMeasurementBoxDetails).details.boxGroup);

    const idx: number | undefined = variableDistanceBox?.measurements.indexOf(measurement);
    if (idx === -1 || idx === undefined) {
      return;
    } else {
      variableDistanceBox?.measurements.splice(idx, 1);
    }
  }

  @action
  public removeMeasurementVisibilityDependencies(measurement: IMeasurementViewModel, object: IObjectViewModel): void {
    const visibilityDependencies: Array<IVisibilityDependency> = this.getVisibilityDependencies();
    const dpIdx: number = visibilityDependencies?.findIndex(
      (dp: IVisibilityDependency) => dp.item === object.descriptor && dp.dependsOn[0] === measurement.descriptor
    );
    if (dpIdx === -1 || dpIdx === undefined) return;
    else visibilityDependencies?.splice(dpIdx, 1);
  }

  @action
  public removeByDescriptor(descriptor: string): void {
    let idx = this.props.measurements.findIndex((m) => m.descriptor === descriptor);
    if (idx > -1) {
      this.props.measurements.splice(idx, 1);
    }
    idx = this.props.objects.findIndex((o) => o.descriptor === descriptor);
    if (idx > -1) {
      this.props.objects.splice(idx, 1);
    }
  }

  public getVmByDescriptor(descriptor: string): IBaseViewModel | undefined {
    let vm: IBaseViewModel | undefined = this.props.measurements.find((m) => m.descriptor === descriptor);
    if (vm) return vm;

    vm = this.props.objects.find((m) => m.descriptor === descriptor);

    return vm;
  }

  public getGhostCursor(): Cursor3D | undefined {
    return this.props.ghostCursor;
  }

  @action
  public setGhostCursor(cursor: Cursor3D | undefined): void {
    this.props.ghostCursor = cursor;
  }

  @action
  public setSyncViewsDescriptor(descriptor: string | undefined): void {
    this.props.syncViewsDescriptor = descriptor;
  }

  @action
  public setApprovedStatus(value: boolean): void {
    this.props.approvedStatus = value;
  }

  public getDescriptor(): string {
    return this.props.descriptor;
  }

  public setDescriptor(value: string): void {
    this.props.descriptor = value;
  }

  public getLabel(): string {
    return this.props.label;
  }

  public getSideLabel(): string {
    return this.props.sideLabel;
  }

  public getImageStackDescriptor(): string {
    return this.props.imageStackDescriptor;
  }

  public getViewOptions(): ViewOptionsStruct {
    return this.props.viewOptions;
  }

  public get descriptor2ViewOption(): Record<string, ViewOption> | undefined {
    return this.props.descriptor2ViewOption;
  }

  public getObjects(): Array<IObjectViewModel> {
    return this.props.objects;
  }

  public getObjectByDescriptor<E = unknown>(descriptor: string): IObjectViewModel<E> | undefined {
    return this.props.objects.find((object) => object.descriptor === descriptor) as unknown as IObjectViewModel<E>;
  }

  public getItemByDescriptor(descriptor: string): IBaseViewModel | undefined {
    return this.getObjectByDescriptor(descriptor) || this.getMeasurementByDescriptor(descriptor);
  }

  public getMeasurementByDescriptor(descriptor: string): IMeasurementViewModel | undefined {
    return this.props.measurements.find((measurement) => measurement.descriptor === descriptor);
  }

  public getVisibilityDependencies(): Array<IVisibilityDependency> {
    return this.props.visibilityDependencies;
  }

  public getSelectionDependencies(): Array<ISelectionDependency> {
    return this.props.selectionDependencies;
  }

  public getApprovedStatus(): boolean {
    return this.props.approvedStatus;
  }

  public getMeasurements(): Array<IMeasurementViewModel> {
    return this.props.measurements;
  }

  public getSelectableItems(): Array<ISelectableItem> {
    const measurements = this.getMeasurements();
    const objects = this.getObjects();
    return new Array<ISelectableItem>().concat(measurements, objects);
  }

  public getViewItems(): Array<IBaseViewModel> {
    const measurements = this.getMeasurements();
    const objects = this.getObjects();
    return new Array<IBaseViewModel>().concat(measurements, objects);
  }

  public getCameraFittingFactor(): number {
    return this.props.cameraFittingFactor;
  }

  @computed
  public get measurementsLength(): number {
    return this.props.measurements.length;
  }

  @computed
  public get clippingPlanes(): Map<ViewPortType, THREE.Plane> {
    return this.props.clippingPlanes;
  }

  @computed
  public get syncViewsDescriptor(): string | undefined {
    return this.props.syncViewsDescriptor;
  }

  @computed
  public get objectsLength(): number {
    return this.props.objects.length;
  }

  @computed
  public get descriptorsMapping(): Array<DescriptorMapItem> | undefined {
    return this.props.descriptorsMapping;
  }

  public getMeasurementBoxes(): Array<MeasurementBoxInfo> {
    return this.props.measurementBoxes;
  }

  public getObjectBoxes(): Array<ObjectBoxInfo> {
    return this.props.objectBoxes;
  }

  public getPlanningEntitiesGroups(): Array<GroupInfo> {
    return this.props.planningEntitiesGroups;
  }

  @computed
  public get workflowIndications(): Array<WFIndication> {
    return this.props.workflowIndications;
  }

  public getCenterLines(): Array<ICenterLineViewModel> {
    return this.props.centerLines;
  }

  public getTuneControls(): Array<TuneControlInfo> {
    return this.props.tuneControls;
  }

  public getActiveCenterLineDescriptor(): string | undefined {
    return this.props.activeCenterLineDescriptor;
  }

  public setActiveCenterLineDescriptor(descriptor: string | undefined): void {
    this.props.activeCenterLineDescriptor = descriptor;
  }

  public get availabilityDescriptor(): string | undefined {
    return this.props.availabilityDescriptor;
  }

  public getOrientationPresets(): Array<IOrientationPreset> {
    return this.props.orientationPresets;
  }

  @action
  public addOrientationPreset(preset: IOrientationPreset): void {
    this.props.orientationPresets.push(preset);
  }

  @action
  public deleteOrientationPreset(descriptor: string): void {
    this.props.orientationPresets = this.props.orientationPresets.filter((preset) => preset.descriptor !== descriptor);
  }

  public getImageSlices(): Array<IImageSliceViewModel> {
    return this.props.imageSlices;
  }

  public getHelpers(): Array<ViewHelper> {
    return this.props.helpers;
  }

  public getImagesRoiConfig(): IImagesRoiConfig | undefined {
    return this.props.imagesRoiConfig;
  }

  public getViewOrientationDescriptor(): ViewOrientationKind {
    return this.props.viewOrientationDescriptor;
  }

  public getCameraOrientationDescriptor(): ViewOrientationKind | undefined {
    return this.props.cameraOrientationDescriptor;
  }

  public getFluoroCurvePlaneDescriptor(): string | undefined {
    return this.props.fluoroCurvePlaneDescriptor;
  }

  public getViewPorts(): Array<ViewPortInfo> {
    return this.props.viewPorts;
  }

  public get3DViewPortDetails(): IThreeDViewPortDetails | undefined {
    const threeDViewPort = this.getViewPorts().find((vp: ViewPortInfo) => {
      return vp.kind === ViewPortKind.ThreeD;
    });
    if (!threeDViewPort) return undefined;
    return threeDViewPort.details as IThreeDViewPortDetails;
  }

  public getTooltipsForViewPort(viewPortDescriptor: string): Array<ViewPortTooltipViewModel> {
    return this.props.viewPortsTooltips.filter((tooltip) => tooltip.viewPort === viewPortDescriptor);
  }

  public get collapseImages(): boolean | undefined {
    return this.props.collapseImages;
  }

  public static fromJSON(json: unknown): ViewEntity {
    const { id, ...props } = json as ViewProps & { id: number | string }; // currently ViewEntity doesn't have an ID
    return new ViewEntity(props, new UniqueEntityID(id));
  }

  public clone(): ViewEntity {
    const { objects, measurements } = this.props;
    // if you found something shallow copied in the view, add deep copy here
    const clonedObjects = objects.map((object) => object.clone());
    const clonedMeasurements = measurements.map((measurement) => measurement.clone());
    return new ViewEntity({
      ...this.props,
      objects: clonedObjects,
      measurements: clonedMeasurements as Array<IMeasurementViewModel>
    });
  }

  // TODO OK consider of better strategy solution for Spline selected mode
  @computed
  public get isAxisCenterlineMode(): boolean {
    return Boolean(this.getCenterLines().find((cl) => cl.descriptor === 'long_axis.centerline'));
  }
}
